import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import './StripeCheckout.scss';
import { PaymentResultSuccess, PaymentResultFailed } from './PaymentResultModal';
import { formatPrice } from '../Utils/formatting';

interface StripeCheckoutProps {
    price: number;
    productName: string;
    clientSecret?: string | null;
    currency?: string; // Optional, default to 'IDR'
    onSuccess: () => void;
    onCancel: () => void;
}

const StripeCheckout: React.FC<StripeCheckoutProps> = ({
    price,
    productName,
    clientSecret,
    currency = 'IDR', // Default to IDR, can be overridden
    onSuccess,
    onCancel
}) => {
    const stripe = useStripe();
    const elements = useElements();
    
    // State for payment result modals
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showFailedModal, setShowFailedModal] = useState(false);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    
    // State for processing
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
            return;
        }

        // Show processing state
        setIsProcessing(true);
        const submitButton = event.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = 'Processing...';
        }

        try {
            if (clientSecret) {
                // If we have a client secret, use it to confirm the payment with Stripe
                // Use the client secret from the API to confirm payment
                const result = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: cardElement,
                        billing_details: {
                            // We could collect these from the user if needed
                        },
                    }
                });

                if (result.error) {
                    // Payment failed - show failed modal
                    setErrorMessage(result.error.message || 'Payment failed');
                    setShowFailedModal(true);
                } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
                    // Payment succeeded - show success modal
                    setPaymentIntentId(result.paymentIntent.id);
                    setShowSuccessModal(true);
                } else {
                    // Unexpected state
                    setErrorMessage('Payment was not completed. Please try again.');
                    setShowFailedModal(true);
                }
            } else {
                // No client secret provided - this is an error case
                console.error('No client secret provided for Stripe payment');
                setErrorMessage('Payment configuration error. Please try again later.');
                setShowFailedModal(true);
            }
        } catch (error) {
            console.error('Payment error:', error);
            setErrorMessage('An unexpected error occurred during payment processing.');
            setShowFailedModal(true);
        } finally {
            setIsProcessing(false);
            // Reset button
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = `Pay ${formatPrice(price, currency)}`;
            }
        }
    };

    // Retry payment handler
    const handleRetry = () => {
        setShowFailedModal(false);
        // Reset card element
        const cardElement = elements?.getElement(CardElement);
        if (cardElement) {
            cardElement.clear();
        }
    };

    // Close success and notify parent
    const handleSuccessClose = () => {
        setShowSuccessModal(false);
        onSuccess();
    };

    return (
        <div className="stripe-checkout-container">
            <div className="stripe-checkout-header">
                <h2>Payment</h2>
                <button
                    className="close-button"
                    onClick={onCancel}
                    disabled={isProcessing}
                >
                    &times;
                </button>
            </div>

            <div className="product-summary">
                <div className="product-name">{productName}</div>
                <div className="product-price">{formatPrice(price, currency)}</div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <label htmlFor="card-element">Credit or debit card</label>
                    <CardElement
                        id="card-element"
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#424770',
                                    '::placeholder': {
                                        color: '#aab7c4',
                                    },
                                },
                                invalid: {
                                    color: '#9e2146',
                                },
                            },
                        }}
                    />
                </div>

                <div className="checkout-actions">
                    <button
                        className="cancel-button"
                        type="button"
                        onClick={onCancel}
                        disabled={isProcessing}
                    >
                        Cancel
                    </button>
                    <button
                        className="pay-button"
                        type="submit"
                        disabled={!stripe || isProcessing}
                    >
                        {isProcessing ? 'Processing...' : `Pay ${formatPrice(price, currency)}`}
                    </button>
                </div>
            </form>

            <div className="stripe-checkout-footer">
                <div className="secure-badge">
                    <span className="lock-icon">🔒</span> Secure payment
                </div>
                <div className="powered-by">
                    Powered by <span className="stripe-text">Stripe</span>
                </div>
            </div>

            {/* Payment Result Modals */}
            <PaymentResultSuccess
                isOpen={showSuccessModal}
                onClose={handleSuccessClose}
                sessionId={paymentIntentId || undefined}
                productName={productName}
                price={price}
            />

            <PaymentResultFailed
                isOpen={showFailedModal}
                onClose={() => setShowFailedModal(false)}
                errorMessage={errorMessage}
                onRetry={handleRetry}
            />
        </div>
    );
};

export default StripeCheckout;